import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { FileType, RadioOrSelectOption, RootState } from '../../../../types/common.types';
import { Comment, HashTag, Post } from '../../../../types/entity.types';

import { useAdministrationApi } from '../../../../api/administration/useAdministrationApi';
import { useApprovalApi } from '../../../../api/approval/useApprovalApi';
import { useCommentApi } from '../../../../api/comment/useCommentApi';
import { usePushNotificationApi } from '../../../../api/notification/push/usePushNotificationApi';
import { usePostApi } from '../../../../api/post/usePostApi';
import { useReactionApi } from '../../../../api/reaction/useReactionApi';

import { setPost } from '../../../../slices/programme/tribe/tribe.slice';

import { ARCHIVED, POST_SEARCH_OPTIONS, TRIBE_PAGE_SIZE } from '../../../../utils/constants';

import TribePost from '../../../../components/screens/programme/tribe/tribe-post/TribePost';

type Props = {
  status: string;
  post: Post;
  hashtags?: HashTag[];
  refreshPost?: (externalReference: string) => void;
  approval?: boolean;
  searchType?: RadioOrSelectOption;
  hierarchy?: boolean;
  setComposing?: (value: boolean) => void;
};

const TribePostContainer = ({
  status,
  post,
  hashtags = [],
  refreshPost,
  setComposing,
  approval = false,
  searchType = POST_SEARCH_OPTIONS[0],
  hierarchy = false
}: Props) => {
  const [files, setFiles] = useState<FileType[]>([]);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [showAllComments, setShowAllComments] = useState(false);

  const { reactions } = useSelector(({ tribe }: RootState) => tribe);
  const { members } = useSelector(({ member }: RootState) => member);
  const { user } = useSelector(({ system }: RootState) => system);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { programmeExternalReference, memberExternalReference } = useParams();

  const { sendPushNotification } = usePushNotificationApi();

  const { viewPosts, updatePost, deletePost: deletePostRequest } = usePostApi();

  const { viewComments } = useCommentApi();

  const { createReaction: createReactionRequest, deleteReaction: deleteReactionRequest } = useReactionApi();

  const { viewHashtagOccurrences } = useAdministrationApi();

  const { updatePostStatus: updatePostStatusRequest, evaluateSupportConversation: evaluateSupportConversationRequest } =
    useApprovalApi();

  const [searchParams] = useSearchParams();

  const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';

  useEffect(() => {
    if (!approval) {
      viewComments(
        {
          postExternalReference: post.externalReference!,
          page: 0,
          size: showAllComments || hierarchy ? 999 : 2
        },
        (data) => setComments(data)
      );
    } else {
      setComments(post.comments ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, post, showAllComments, approval]);

  useEffect(() => {
    setFiles((post.linkedMediaFiles as FileType[]) ?? []);
  }, [post.linkedMediaFiles]);

  useEffect(() => {
    const filteredReactions = reactions.filter((reaction) => reaction.postExternalReference === post.externalReference);

    setLiked(filteredReactions.some((reaction) => reaction.reaction === 'like'));
  }, [reactions, post]);

  const pinPost = () => {
    if (status === ARCHIVED) return;

    const pinned = !post.pinned;

    updatePost(
      {
        ...post,
        postExternalReference: post.externalReference!,
        pinned
      },
      () => {
        refreshPost?.(post.externalReference!);
      }
    );
  };

  const editPost = () => {
    dispatch(setPost(post));
    setComposing?.(true);
  };

  const deletePost = () => {
    deletePostRequest(
      {
        postExternalReference: post.externalReference!
      },
      () => {
        viewPosts({
          programmeExternalReference: programmeExternalReference!,
          page: 0,
          size: TRIBE_PAGE_SIZE,
          searchType: searchType?.value
        });
        viewHashtagOccurrences({ programmeExternalReference: programmeExternalReference! });
      }
    );
  };

  const createReaction = (reaction: string) => {
    createReactionRequest(
      {
        postExternalReference: post.externalReference!,
        programmeExternalReference: programmeExternalReference!,
        reaction
      },
      () => {
        refreshPost?.(post.externalReference as string);

        if (reaction !== 'like' || user?.username === post.userName || !post.memberExternalReference) return;

        sendPushNotification({
          recipientExternalReference: post.memberExternalReference,
          title: `${user?.username} liked your post`,
          data: JSON.stringify({
            screen: 'tribe-post-hierarchy',
            navigator: 'programme',
            params: { externalReference: post.externalReference },
            programmeExternalReference
          })
        });
      }
    );
  };

  const deleteReaction = (reaction: string) => {
    const member = members.find((member) => member.username === user?.username);

    if (!member) return;

    deleteReactionRequest(
      {
        postExternalReference: post.externalReference!,
        memberExternalReference: member.memberExternalReference!,
        reaction
      },
      () => refreshPost?.(post.externalReference as string)
    );
  };

  const updatePostStatus = (status: string, onSuccess: () => void) => {
    updatePostStatusRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        postExternalReference: post.externalReference!,
        status
      },
      onSuccess
    );
  };

  const evaluateSupportConversation = (systemUserExternalReference: string) => {
    evaluateSupportConversationRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        systemUserExternalReference
      },
      () =>
        navigate(
          `/programmes/${status}/${programmeExternalReference}/support/${systemUserExternalReference}?hasSubProgrammes=${hasSubProgrammes}`
        )
    );
  };

  const navigateToProfile = (username: string) => {
    const member = members.find((member) => member.username === username);

    if (!member || memberExternalReference === member.memberExternalReference) return;

    navigate(
      `/programmes/${status}/${programmeExternalReference}/tribe/${member.memberExternalReference}?hasSubProgrammes=${hasSubProgrammes}`
    );
  };

  return (
    <TribePost
      status={status}
      post={post}
      hashtags={hashtags}
      files={files}
      liked={liked}
      comments={comments}
      approval={approval}
      showAllComments={showAllComments}
      setShowAllComments={setShowAllComments}
      pinPost={pinPost}
      editPost={editPost}
      deletePost={deletePost}
      createReaction={createReaction}
      deleteReaction={deleteReaction}
      refreshPost={refreshPost}
      updatePostStatus={updatePostStatus}
      evaluateSupportConversation={evaluateSupportConversation}
      hierarchy={hierarchy}
      navigateToProfile={navigateToProfile}
    />
  );
};

export default TribePostContainer;
